import { useMemo } from 'react';

import { useIntl } from 'react-intl';

import { IRestaurantNode } from 'generated/rbi-graphql';
import { useQueueInformationFormatter } from 'hooks/queue';
import { useFlag } from 'state/launchdarkly';
import { LaunchDarklyFlag } from 'utils/launchdarkly';
import { useGetRestaurantAvailabilityFn, useIsMobileOrderingAvailable } from 'utils/restaurant';

interface UseErrorMessageOptions {
  restaurant: IRestaurantNode;
}

export const useErrorMessage = ({ restaurant }: UseErrorMessageOptions) => {
  const { formatMessage } = useIntl();

  const getRestaurantAvailability = useGetRestaurantAvailabilityFn();
  const isMobileOrderingAvailable = useIsMobileOrderingAvailable(restaurant);

  const isRestaurantAvailable = getRestaurantAvailability(restaurant);
  const hideMobileOrdering = useFlag(LaunchDarklyFlag.HIDE_MOBILE_ORDERING);

  const { waitTimeWarningOptions } = useQueueInformationFormatter(restaurant.waitTime);

  return useMemo(() => {
    if (!restaurant.hasMobileOrdering) {
      if (!hideMobileOrdering) {
        return formatMessage({ id: 'mobileOrderingUnavailableAtStore' });
      }
    } else if (!isMobileOrderingAvailable || !isRestaurantAvailable) {
      if (!hideMobileOrdering) {
        return formatMessage({ id: 'mobileOrderingUnavailable' });
      }
    } else if (
      waitTimeWarningOptions?.atStoreLocator &&
      restaurant.waitTime?.queueLength &&
      restaurant.waitTime?.queueLength > 0
    ) {
      return formatMessage({ id: 'virtualQueueCurrentlyBusyLongerWaitTimesExpectedWarning' });
    }

    return;
  }, [
    formatMessage,
    hideMobileOrdering,
    isMobileOrderingAvailable,
    isRestaurantAvailable,
    restaurant.hasMobileOrdering,
    restaurant.waitTime,
    waitTimeWarningOptions,
  ]);
};
